/**
 * @file MeetingsLibraryPage.tsx
 * @brief The user's meeting list, the phone's "Boardrooms" screen.
 *
 * @details
 * DES, 30-09-2026: web and desktop get the phone's experience. This is
 * `FRONTEND/src/screens/MeetingIntelligence/BoardroomSessionsScreen.tsx`,
 * checked against the simulator:
 * - "Boardrooms" (the phone's header shows the title alone).
 * - The Ask pill plus a calendar circle to My Meetings.
 * - The interrupted-draft card.
 * - "Upcoming": the next scheduled meeting, or "Nothing upcoming · See past
 *   and cancelled meetings".
 * - Date sections (Today, Yesterday, This Week, This Month, month) with a
 *   timeline node per meeting: red when ready, error-red when failed, grey
 *   while processing.
 * - Title and "30 Sep at 16:13" above a white card. The card carries the
 *   duration and status tabs on its top edge; "Organised by …"; a two-line
 *   overview; the "Speakers aren't resolved yet" hint; #tags; and the avatar
 *   stack with 👥 N, 🌐 N and 🔖 N chips.
 * - A bottom dock: a wide search plus a round "+" (New boardroom), 08-10-2026.
 * - The phone's long-press options come from the web's existing
 *   `MeetingRowActions` ("⋯"). It already enforces who may delete, so the
 *   corporate-employee rule stays server- and scope-driven.
 *
 * The list grows as you scroll (20 at a time), like the phone.
 *
 * @author DES
 * @date 30-09-2026
 */

import { failureBadgeLabel, failureReasonOf } from "@/features/company-meetings/meetingFailure";
import { HintBubble, HintIcon } from "@/components/ui/HintPopover";
import { useHintPopover } from "@/components/ui/useHintPopover";
import { EmployeePicker } from "@/features/company-sessions/components/EmployeePicker";
import { PersonPhoto } from "@/components/ui/PersonPhoto";
import { EmptyState } from "@/components/feedback/EmptyState";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  AudioLines,
  Bookmark,
  Calendar,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Clock,
  Languages,
  Loader2,
  Plus,
  Table2,
  Tag,
  Users,
  UsersRound,
  X,
} from "lucide-react";
import { AskSpark } from "@/components/icons/AskSpark";
import { apiGet } from "@/shared/api/client";
import { boardroomEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { ExpandingSearch } from "@/components/ui/ExpandingSearch";
import { cn } from "@/shared/theme/cn";
import { dockFadeBehind, dockGlassDark } from "@/shared/theme/dockGlass";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import { isRTLText } from "@/shared/utils/formatters";
import { companyMeetingsKeys } from "@/features/company-meetings/api";
import {
  isNotesTierMeeting,
  MeetingsListResponseSchema,
  type MeetingRow,
} from "@/features/company-meetings/schemas";
import { MeetingSharingLine } from "./MeetingSharingLine";
import {
  MeetingOwnerLine,
  MineTeamSwitch,
} from "@/features/company-meetings/components/MineTeamSwitch";
import {
  TEAM_EMPTY_TITLE,
  useMeetingListScope,
  type MeetingListScope,
} from "@/features/company-meetings/meetingListScope";
import { MeetingRowActions } from "@/features/company-meetings/components/MeetingRowActions";
import { AskMeetingsPanel } from "@/features/company-meetings/components/AskMeetingsPanel";
import { AskPalette } from "@/features/company-meetings/components/AskPalette";
import { noteDateLabel } from "@/features/notes/noteGrouping";
import { formatWhen } from "@/features/notes/noteFormat";
import { TagsSubmenu } from "@/features/notes/TagsMenu";
import { InterruptedDraftBanner } from "./InterruptedDraftBanner";
import { TagFilterItems } from "./meetingListTools";
import { DateRangePopover } from "@/components/ui/DateRangePopover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import {
  datePresetDefs,
  rangeFor,
  rangeLabel,
  useSaveMeetingTags,
  type RangePreset,
} from "./meetingListLogic";
import { listScheduledMeetings } from "./scheduledMeetingsApi";
import { CalendarMeetingsPanel } from "./CalendarMeetingsPanel";
import { withoutScheduled } from "./calendarMeetings";
import { useDesktopCalendar } from "./useDesktopCalendar";
import { formatSessionDuration } from "@/shared/utils/duration";

const PAGE_SIZE = 20;
const DATE_PRESETS = datePresetDefs();
/** An empty, unfiltered This month shows the latest few instead (the phone's rule). */
const RECENT_FALLBACK_COUNT = 5;
/** Node centre: the title's first line (20px bold, 28px line). */
const NODE_TOP = 14;

type MeetingExtras = MeetingRow & {
  tags?: string[] | null;
  meeting_summary?: string | null;
};

const STATUS_LABEL: Record<string, string> = {
  ready: "Ready",
  resolved_pending: "Resolve speakers",
  synthesis_processing: "Compiling",
  processing: "Processing",
  uploading: "Uploading",
  error: "Failed",
};

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Setup participants, else attendees: the people the phone draws. */
function people(m: MeetingRow) {
  const src = (m.attendees?.length ? m.attendees : m.participants) ?? [];
  const seen = new Set<string>();
  return src.filter((p) => {
    const key = (p.name ?? "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

type ListFilters = {
  search: string;
  /** null: no date bound (All company meetings starts on all time). */
  from: Date | null;
  to: Date | null;
  tags: string[];
  /** This month, no search, no tag: an empty page falls back to the latest few. */
  isDefault: boolean;
  /** All company meetings: one employee's meetings (company admin only). */
  employeeId?: string;
  /** "Mine | Team" (22-10-2026), company admins on Boardrooms only. */
  scope?: MeetingListScope;
};

function useMeetingsInfinite(f: ListFilters) {
  return useInfiniteQuery({
    // Under the list prefix, so every delete/rename that refreshes the
    // table refreshes this too.
    queryKey: [
      ...companyMeetingsKeys.all,
      "list",
      {
        infinite: true,
        search: f.search,
        from: f.from?.toISOString() ?? null,
        to: f.to?.toISOString() ?? null,
        tags: f.tags,
        employeeId: f.employeeId ?? null,
        scope: f.scope ?? null,
      },
    ] as const,
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({ page: String(pageParam), pageSize: String(PAGE_SIZE) });
      if (f.search) qs.set("search", f.search);
      if (f.from) qs.set("from", f.from.toISOString());
      if (f.to) qs.set("to", f.to.toISOString());
      for (const t of f.tags) qs.append("tag", t);
      if (f.employeeId) qs.set("employee_id", f.employeeId);
      if (f.scope) qs.set("scope", f.scope);
      const raw = await apiGet<{ available_tags?: unknown }>(`${boardroomEndpoints.list}?${qs}`);
      const parsed = MeetingsListResponseSchema.parse(raw);
      const availableTags = Array.isArray(raw?.available_tags)
        ? raw.available_tags.filter((t): t is string => typeof t === "string")
        : [];
      if (pageParam === 1 && f.isDefault && parsed.meetings.length === 0) {
        const recent = MeetingsListResponseSchema.parse(
          await apiGet<unknown>(
            `${boardroomEndpoints.list}?page=1&pageSize=${RECENT_FALLBACK_COUNT}${
              f.scope ? `&scope=${f.scope}` : ""
            }`,
          ),
        );
        return {
          meetings: recent.meetings.slice(0, RECENT_FALLBACK_COUNT) as MeetingExtras[],
          page: 1,
          total: recent.meetings.length,
          hasMore: false,
          availableTags,
        };
      }
      return {
        meetings: parsed.meetings as MeetingExtras[],
        page: Number(parsed.page ?? pageParam),
        total: Number(parsed.total ?? 0),
        hasMore: Boolean(parsed.hasMore),
        availableTags,
      };
    },
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    placeholderData: (prev) => prev,
  });
}

// ----------------------------------------------------------------------
// One meeting, on the timeline
// ----------------------------------------------------------------------
function MeetingTimelineRow({
  meeting,
  first,
  last,
  base,
  scope,
  tagsMenu,
  teamScope = false,
}: {
  meeting: MeetingExtras;
  first: boolean;
  last: boolean;
  base: string;
  scope: "employee" | "company";
  /** The admin's Team list: the owner's name and photo on the card. */
  teamScope?: boolean;
  /** The ⋯ menu's "Tags" submenu for this meeting (DES 04-10-2026). */
  tagsMenu: (m: MeetingExtras) => ReactNode;
}) {
  const navigate = useNavigate();
  const status = meeting.status;
  const reason = failureReasonOf(meeting as { status?: string | null; failure_reason?: unknown });
  // "No speech" / "Timed out" for a stuck meeting the server stopped (18-10-2026).
  const label = reason && reason !== "processing" ? failureBadgeLabel(reason) : (STATUS_LABEL[status] ?? "In progress");
  const needsSpeakers = status === "resolved_pending";
  const redPair = status === "ready" || status === "error" || needsSpeakers;
  const who = people(meeting);
  const langs = meeting.target_languages ?? [];
  const bookmarks = meeting.bookmarks ?? [];
  const tags = meeting.tags ?? [];
  const snippet = (meeting.meeting_overview || meeting.meeting_summary || "").trim();
  const title = meeting.title?.trim() || "Untitled meeting";
  const organised = meeting.organised_by;
  const startedByOther = Boolean(meeting.started_by) && meeting.started_by !== organised;
  const owner = teamScope ? (meeting.owner ?? null) : null;
  // "Ali's meeting" says who; "Organised by Ali" again would repeat it.
  const showOrganised =
    Boolean(organised) &&
    !(meeting.is_owner && !startedByOther) &&
    !(owner && !startedByOther && organised === owner.name);
  const open = () => navigate(`${base}/meetings/${meeting.id}`);

  return (
    <div className="flex gap-2">
      <div className="relative w-6 shrink-0" aria-hidden>
        {!first ? (
          <span
            className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
            style={{ height: NODE_TOP }}
          />
        ) : null}
        {!last ? (
          <span
            className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
            style={{ top: NODE_TOP }}
          />
        ) : null}
        <span
          className={cn(
            "absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-app-page",
            status === "ready" || needsSpeakers
              ? "bg-brand"
              : status === "error"
                ? "bg-destructive"
                : "bg-muted-foreground/50",
          )}
          style={{ top: NODE_TOP }}
        />
      </div>

      <div className="min-w-0 flex-1 pb-6">
        <button type="button" onClick={open} className="block w-full text-left">
          <span
            dir={isRTLText(title) ? "rtl" : "ltr"}
            className="line-clamp-2 text-xl font-bold leading-7 text-app-text"
          >
            {title}
          </span>
          <span className="mt-0.5 block text-[15px] font-semibold text-app-text-secondary">
            {formatWhen(meeting.started_at)}
          </span>
        </button>

        <div className="relative mt-[22px]">
          <div className="absolute -top-2.5 right-6 z-10 flex items-center gap-1.5">
            {/* A shared Meeting Notes recording (sharing v2, 21-10-2026). */}
            {isNotesTierMeeting(meeting) ? (
              <span className="inline-flex items-center rounded-full bg-app-capsule px-2.5 py-[3px] text-[11px] font-bold tracking-[0.5px] text-app-text-secondary shadow-[0_1px_2px_rgba(0,0,0,0.1)]">
                Notes
              </span>
            ) : null}
            {/* Status only: the duration sits at the chips row's end as
                "⏱ 26m", as on the phone and the Home cards (DES 04-10-2026). */}
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-[3px] text-[11px] font-bold tracking-[0.5px] shadow-[0_1px_2px_rgba(0,0,0,0.1)]",
                redPair ? "bg-[#FEF2F2] dark:bg-[#2D1414]" : "bg-app-capsule",
                status === "error"
                  ? "text-destructive"
                  : redPair
                    ? "text-brand"
                    : "text-app-text-secondary",
              )}
            >
              {label}
            </span>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={open}
            onKeyDown={(e) => {
              if (e.key === "Enter") open();
            }}
            className="cursor-pointer rounded-2xl bg-app-card p-4 transition-shadow hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]"
          >
            {owner ? <MeetingOwnerLine owner={owner} className="mb-2" /> : null}
            {showOrganised ? (
              <p className="mb-2 flex items-start gap-1 whitespace-pre-line text-xs font-medium text-brand">
                <UsersRound className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                {startedByOther
                  ? `Organised by ${organised}\nStarted by ${meeting.started_by}`
                  : `Organised by ${organised}`}
              </p>
            ) : null}
            {snippet ? (
              <p
                dir={isRTLText(snippet) ? "rtl" : "ltr"}
                className="mb-3 line-clamp-2 text-sm leading-5 text-app-text-secondary"
              >
                {snippet}
              </p>
            ) : null}
            {needsSpeakers ? (
              <p className="mb-2 flex items-start gap-1 text-xs font-medium text-brand">
                <AudioLines className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                Speakers aren&apos;t resolved yet. Tap to match each voice.
              </p>
            ) : null}
            {tags.length ? (
              <p className="mb-3 flex flex-wrap gap-x-3 gap-y-1.5 text-[13px] font-medium text-app-text-secondary">
                {tags.slice(0, 3).map((t) => (
                  <span key={t}>
                    <b className="font-bold">#</b>
                    {t}
                  </span>
                ))}
                {tags.length > 3 ? <span>+{tags.length - 3}</span> : null}
              </p>
            ) : null}
            {/* Who has it (sharing v2, DES 21-10-2026): owner and admin
                open "Who has access"; a shared person reads "Shared by". */}
            <MeetingSharingLine
              meetingId={meeting.id}
              sharing={meeting.sharing}
              className="mb-3"
            />
            <div className="flex items-center gap-2">
              {who.length ? (
                <span className="flex">
                  {/* At most three circles, "+N" included: two faces + "+N"
                      once there are more than three (DES 04-10-2026). */}
                  {who.slice(0, who.length > 3 ? 2 : 3).map((p, i) => (
                    <span
                      key={`${p.name}-${i}`}
                      title={p.name ?? ""}
                      className={cn(
                        "flex h-[34px] w-[34px] items-center justify-center overflow-hidden rounded-full border-2 border-app-card text-xs font-bold text-app-text",
                        p.isExternal ? "bg-brand/10" : "bg-app-quaternary",
                        i > 0 && "-ml-2.5",
                      )}
                    >
                      <PersonPhoto src={p.profileImageUrl} fallback={initials(p.name ?? "")} />
                    </span>
                  ))}
                  {who.length > 3 ? (
                    <span className="-ml-2.5 flex h-[34px] w-[34px] items-center justify-center rounded-full border-2 border-app-card bg-app-quaternary-dark text-xs font-bold text-app-text">
                      +{who.length - 2}
                    </span>
                  ) : null}
                </span>
              ) : null}
              {[
                { icon: Users, n: who.length, show: true, title: "Participants" },
                { icon: Languages, n: langs.length, show: true, title: "Languages" },
                {
                  icon: Bookmark,
                  n: bookmarks.length,
                  show: bookmarks.length > 0,
                  title: "Bookmarks",
                },
              ]
                .filter((c) => c.show)
                .map(({ icon: Icon, n, title: t }) => (
                  <span
                    key={t}
                    title={t}
                    className="inline-flex items-center gap-[5px] rounded-full bg-black/[0.04] px-2 py-1 text-[13px] font-semibold text-app-text-secondary dark:bg-white/[0.06]"
                  >
                    <Icon className="h-4 w-4" strokeWidth={1.5} />
                    {n}
                  </span>
                ))}
              <span className="ml-auto flex shrink-0 items-center gap-1">
                <Clock className="h-3.5 w-3.5 text-app-text-secondary" strokeWidth={1.5} />
                <span className="text-[13px] font-semibold text-app-text-secondary">
                  {formatSessionDuration(meeting.total_duration_seconds)}
                </span>
              </span>
              {/* The phone's long-press options, as the web's row menu. */}
              <span onClick={(e) => e.stopPropagation()}>
                <MeetingRowActions
                  meeting={meeting}
                  scope={scope}
                  onView={open}
                  // Owner only, as the server enforces (a company admin reads
                  // a colleague's meeting but cannot tag or share it).
                  tagsMenu={meeting.is_owner === false ? undefined : tagsMenu(meeting)}
                  // Share… / Who has access from the list's `sharing`
                  // (owner both, company admin the list; 22-10-2026).
                  withShare
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Toolbar circle: the phone's icon-only QuickActionPill + FilterClearBadge
// ----------------------------------------------------------------------
/**
 * Loading placeholder in MeetingTimelineRow's shape (the phone's
 * SkeletonBoardroomCard): rail + node, title and date beside it, tabs on the
 * card's edge, organiser line, two snippet lines, avatars and chips.
 * Web-only skeleton (DES 01-10-2026).
 */
function MeetingsListSkeleton({ rows = 3 }: { rows?: number }) {
  const bone = (cls: string) => <Skeleton still className={cls} />;
  return (
    <div role="status" aria-label="Loading meetings" className="animate-pulse">
      {bone("mb-3 mt-5 h-5 w-24")}
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex gap-2">
          <div className="relative w-6 shrink-0" aria-hidden>
            {i > 0 ? (
              <span
                className="absolute left-1/2 top-0 w-0.5 -translate-x-1/2 bg-border"
                style={{ height: NODE_TOP }}
              />
            ) : null}
            {i < rows - 1 ? (
              <span
                className="absolute bottom-0 left-1/2 w-0.5 -translate-x-1/2 bg-border"
                style={{ top: NODE_TOP }}
              />
            ) : null}
            <Skeleton
             
              still
              className="absolute left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full"
              style={{ top: NODE_TOP }}
            />
          </div>
          <div className="min-w-0 flex-1 pb-6">
            {bone("mt-1 h-5 w-3/4")}
            {bone("mt-2 h-4 w-32")}
            <div className="relative mt-[22px]">
              <div className="absolute -top-2.5 right-6 z-10 flex gap-1.5">
                {bone("h-[19px] w-14 rounded-full")}
                {bone("h-[19px] w-16 rounded-full")}
              </div>
              <div className="rounded-2xl bg-app-card p-4">
                {bone("mb-3 h-3 w-40")}
                {bone("mb-2 h-3.5 w-full")}
                {bone("mb-4 h-3.5 w-3/4")}
                <div className="flex items-center gap-2">
                  <span className="flex">
                    {[0, 1, 2].map((a) => (
                      <Skeleton
                        key={a}
                       
                        still
                        className={cn(
                          "h-[34px] w-[34px] rounded-full border-2 border-app-card",
                          a > 0 && "-ml-2.5",
                        )}
                      />
                    ))}
                  </span>
                  {bone("h-[26px] w-14 rounded-full")}
                  {bone("h-[26px] w-14 rounded-full")}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FilterCircle({
  icon: Icon,
  label,
  active,
  onClear,
  clearLabel,
  children,
  onClick,
  wrap,
}: {
  icon: typeof Calendar;
  label: string;
  active: boolean;
  onClear: () => void;
  clearLabel: string;
  /** The dropdown's items (DES 30-09-2026: a menu, like the row's "⋯"). */
  children?: ReactNode;
  /** Instead of a dropdown: the circle is handed to `wrap` (the date popover). */
  onClick?: () => void;
  wrap?: (circle: ReactNode) => ReactNode;
}) {
  const circle = (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        "flex h-[68px] w-[68px] items-center justify-center rounded-full border transition-colors",
        active
          ? "border-brand bg-brand hover:bg-brand/90"
          : "border-black/5 bg-black/[0.04] hover:bg-black/[0.08] dark:bg-white/[0.06]",
      )}
    >
      <Icon className={cn("h-9 w-9", active ? "text-white" : "text-brand")} strokeWidth={1.5} />
    </button>
  );
  return (
    <span className="relative shrink-0">
      {wrap ? (
        wrap(circle)
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>{circle}</DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {children}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
      {active ? (
        <button
          type="button"
          onClick={onClear}
          aria-label={clearLabel}
          title={clearLabel}
          className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full border-2 border-app-page bg-app-text text-app-page"
        >
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
        </button>
      ) : null}
    </span>
  );
}

// ----------------------------------------------------------------------
// Page
// ----------------------------------------------------------------------
/**
 * `allCompany` (DES 01-10-2026: "All company meetings ... bakiyoon jasa
 * kiyoon nahin hain", answered "Boardrooms jaisa"): the same page for a
 * company admin's /company/meetings/all, titled "All company meetings", with
 * an employee pill beside the date and tag circles and a way back to
 * Boardrooms. The list API already returns every company meeting to an admin;
 * the pill adds `employee_id`. It replaces the table (`CompanyMeetingsPage`).
 */
export function MeetingsLibraryPage({ allCompany = false }: { allCompany?: boolean } = {}) {
  const { entitlements, user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The Mac's calendar, desktop app only (06-10-2026); hidden everywhere else.
  const calendar = useDesktopCalendar({ userId: user?.id, selfEmail: user?.email });
  const base = pathname.startsWith("/company") ? "/company" : "/app";
  const scope = base === "/company" ? "company" : "employee";
  const boardroomOff = entitlements.boardroom === false;
  const [searchParams, setSearchParams] = useSearchParams();
  const urlSearch = searchParams.get("search") ?? "";
  const [draft, setDraft] = useState(urlSearch);
  const [askOpen, setAskOpen] = useState(false);
  // The phone's tips icon, pinned at the toolbar's end (DES 05-10-2026).
  const tips = useHintPopover("boardroom_list_tips");

  // 350ms like the phone, then into the URL.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = draft.trim();
      if (next === urlSearch) return;
      const usp = new URLSearchParams(searchParams);
      if (next) usp.set("search", next);
      else usp.delete("search");
      setSearchParams(usp, { replace: true });
    }, 350);
    return () => window.clearTimeout(t);
  }, [draft, urlSearch, searchParams, setSearchParams]);

  // Date range: This month by default, as on the phone (28-09-2026).
  // All company meetings starts on all time (null), as the table it replaces
  // did; Boardrooms keeps This month.
  type Range = { preset: RangePreset; from: Date; to: Date };
  const defaultRange = (): Range | null =>
    allCompany ? null : { preset: "month", ...rangeFor("month") };
  const [rangeState, setRange] = useState<Range | null>(defaultRange);
  const range: Range = rangeState ?? { preset: "month", ...rangeFor("month") };
  const [filterTags, setFilterTags] = useState<string[]>([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const saveTags = useSaveMeetingTags();

  const employeeId = allCompany ? searchParams.get("employee_id") ?? undefined : undefined;
  // Mine | Team (22-10-2026): not on All company meetings, which is the
  // admin's whole-company view with its own employee filter.
  const listScope = useMeetingListScope();
  const scopeParam = allCompany ? undefined : listScope.scope;
  const teamScope = scopeParam === "team";
  const setEmployee = (id: string | null) => {
    const usp = new URLSearchParams(searchParams);
    if (id) usp.set("employee_id", id);
    else usp.delete("employee_id");
    setSearchParams(usp, { replace: true });
  };
  const list = useMeetingsInfinite({
    employeeId,
    scope: scopeParam,
    search: urlSearch,
    // ⛔ A tag searches the whole archive, never the date range (DES
    // 03-10-2026), as on the phone: a June tag must not come back empty
    // under "This month".
    from: rangeState && !filterTags.length ? rangeState.from : null,
    to: rangeState && !filterTags.length ? rangeState.to : null,
    tags: filterTags,
    isDefault:
      !allCompany && range.preset === "month" && !urlSearch && filterTags.length === 0,
  });
  const meetings = useMemo(() => list.data?.pages.flatMap((p) => p.meetings) ?? [], [list.data]);
  // Kept between loads, as the phone keeps its state: a failed or loading
  // page must not hide the tag filter the user is using.
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const freshTags = list.data?.pages[0]?.availableTags;
  useEffect(() => {
    if (freshTags) setAvailableTags(freshTags);
  }, [freshTags]);
  const dateActive =
    !filterTags.length && (allCompany ? rangeState !== null : range.preset !== "month");
  // Filtered results are remembered apart from the plain list.
  const meetingsSkeleton = useLoadingSkeleton(
    urlSearch || filterTags.length || dateActive || employeeId ? "meetings.filtered" : "meetings",
    list.isLoading,
    list.data ? meetings.length : undefined,
  );
  const dateText = rangeState && !filterTags.length
    ? rangeLabel(rangeState.preset, rangeState.from, rangeState.to)
    : "All time";
  const resetDate = () => setRange(defaultRange());

  const upcoming = useQuery({
    queryKey: ["scheduled-meetings", "upcoming"],
    enabled: !boardroomOff,
    staleTime: 60_000,
    queryFn: () => listScheduledMeetings("upcoming"),
  });
  const next = (upcoming.data ?? [])
    .filter((m) => m.status === "scheduled")
    .sort((a, b) => Date.parse(a.scheduled_at) - Date.parse(b.scheduled_at));
  // A Verblizr scheduled meeting is in the calendar too (we email its invite);
  // it stays under Upcoming only, so Record cannot start a second boardroom.
  const calendarMeetings = useMemo(
    () => withoutScheduled(calendar.meetings, upcoming.data ?? []),
    [calendar.meetings, upcoming.data],
  );

  const sections = useMemo(() => {
    const now = Date.now();
    const out: { label: string; items: MeetingExtras[] }[] = [];
    for (const m of meetings) {
      const ms = m.started_at ? Date.parse(m.started_at) : NaN;
      const label = Number.isFinite(ms) ? noteDateLabel(ms, now) : "Earlier";
      const lastSection = out[out.length - 1];
      if (lastSection && lastSection.label === label) lastSection.items.push(m);
      else out.push({ label, items: [m] });
    }
    return out;
  }, [meetings]);

  const sentinel = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && list.hasNextPage && !list.isFetchingNextPage) {
          void list.fetchNextPage();
        }
      },
      { rootMargin: "600px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [list]);

  return (
    <div className="flex min-h-[calc(100dvh-5rem)] w-full flex-col px-5 pb-6 pt-6 md:px-8">
      {/* The phone's header shows the title alone. A company admin also gets
          the table of every company meeting (filters by employee and date). */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <h1 className="text-[22px] font-bold text-app-text">
            {allCompany ? "All company meetings" : "Boardrooms"}
          </h1>
          {allCompany ? null : <MineTeamSwitch />}
        </div>
        {allCompany ? (
          <Link
            to="/company/meetings"
            className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3.5 py-1.5 text-[13px] font-semibold text-app-text-secondary transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06]"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
            Boardrooms
          </Link>
        ) : scope === "company" && !boardroomOff ? (
          <Link
            to="/company/meetings/all"
            className="inline-flex items-center gap-1.5 rounded-full bg-black/[0.04] px-3.5 py-1.5 text-[13px] font-semibold text-app-text-secondary transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06]"
          >
            <Table2 className="h-4 w-4" strokeWidth={1.75} />
            All company meetings
          </Link>
        ) : null}
      </div>

      {boardroomOff ? (
        <p className="mt-8 text-[15px] text-app-text-secondary">
          Meeting Intelligence is turned off for your company. Ask a company admin to enable
          Boardroom under Features.
        </p>
      ) : (
        <>
          {/* Ask + the filter circles, the phone's top row (26-09-2026). A
              filter that is on is a solid brand circle with a corner ✕. My
              Meetings is the "Upcoming" line below, as on the phone. */}
          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setAskOpen(true)}
              className="flex h-[68px] min-w-0 flex-1 items-center gap-3 rounded-full bg-black/[0.04] pl-5 text-left transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06] sm:w-80 sm:flex-none"
            >
              {/* The VERBLIZR logo (DES 09-10-2026); rolls in once as the page
                  opens, as the phone's Boardrooms Ask (index.css). */}
              <AskSpark className="vz-ask-roll-right h-9 w-9 shrink-0 text-brand" />
              <span className="text-[17px] font-medium text-app-text">Ask AI</span>
            </button>
            {/* Desktop: Ask on the left, the filter circles on the page's right
                edge (DES 30-09-2026). On a phone Ask still fills the row. */}
            <span className="hidden flex-1 sm:block" aria-hidden />
            {allCompany ? (
              <EmployeePicker
                variant="pill"
                tall
                value={employeeId ?? null}
                onChange={setEmployee}
              />
            ) : null}
            <FilterCircle
              icon={Calendar}
              label={`Date range: ${dateText}`}
              active={dateActive}
              onClear={resetDate}
              clearLabel="Clear date range"
              onClick={() => setCalendarOpen((o) => !o)}
              // The desktop range picker (DES 30-09-2026): presets on the
              // left, two months, Clear / Filter, under the circle.
              wrap={(circle) => (
                <DateRangePopover
                  open={calendarOpen}
                  onOpenChange={setCalendarOpen}
                  anchor={circle}
                  presets={DATE_PRESETS}
                  value={{
                    presetId: !rangeState || rangeState.preset === "custom" ? null : rangeState.preset,
                    from: rangeState?.from ?? null,
                    to: rangeState?.to ?? null,
                  }}
                  onApply={(v) =>
                    setRange({
                      preset: (v.presetId as RangePreset | null) ?? "custom",
                      from: v.from,
                      to: v.to,
                    })
                  }
                  onClear={resetDate}
                />
              )}
            />
            {/* Always shown (DES 30-09-2026: "filter by tags kidhar hai").
                The phone hides it until a meeting has a tag, which left a
                new user with no way to find the feature. */}
            <FilterCircle
              icon={Tag}
              label={
                filterTags.length
                  ? `Tag filter: ${filterTags.length} active`
                  : "Tag filter: any tag"
              }
              active={filterTags.length > 0}
              onClear={() => setFilterTags([])}
              clearLabel="Clear tag filter"
            >
              <TagFilterItems
                available={availableTags}
                selected={filterTags}
                onChange={setFilterTags}
              />
            </FilterCircle>
            <HintIcon open={tips.open} onClick={tips.toggle} anchorRef={tips.anchorRef} />
          </div>
          {tips.open ? (
            <HintBubble
              lines={[
                "Use ⋯ on a meeting for tags, sharing, export and delete.",
                "Click Ask AI to question all your meetings at once.",
              ]}
              onClose={tips.close}
              anchorRef={tips.anchorRef}
              className="mt-3"
            />
          ) : null}
          {/* The phone says the range only to a screen reader; a mouse user
              gets it in words, so a narrowed list never looks empty by mistake. */}
          {dateActive || filterTags.length ? (
            <p className="mt-2 px-1 text-[13px] font-semibold text-app-text-secondary">
              {dateText}
              {filterTags.length ? `  ·  ${filterTags.map((t) => `#${t}`).join(", ")}` : ""}
            </p>
          ) : null}

          <div className="mt-4">
            <InterruptedDraftBanner />
          </div>

          {/* Two columns on a wide screen (DES 30-09-2026: "page balance
              karnay kay liyeh"): the meetings on the left, Upcoming pinned on
              the right. On a phone it stays one column, Upcoming first, as
              in the app. */}
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_22rem] lg:gap-10">
            <aside className="lg:order-2">
              <div className="lg:sticky lg:top-4">
                <h2 className="mb-1 mt-5 text-lg font-semibold tracking-[-0.5px] text-app-text/40">
                  Upcoming
                </h2>
                {/* The phone's single line. */}
                <div className={cn(next.length > 0 && "lg:hidden")}>
                  <Link
                    to={`${base}/meetings/scheduled`}
                    className="flex items-center gap-3 rounded-2xl py-2 transition-opacity active:opacity-60"
                  >
                    <CalendarClock className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
                    {/* Loading: two bars, not "Nothing upcoming" before
                        the answer is in (01-10-2026). */}
                    {upcoming.isLoading ? (
                      <span role="status" aria-label="Loading upcoming" className="min-w-0 flex-1 animate-pulse space-y-2 py-1">
                        <Skeleton still className="h-[18px] w-2/5" />
                        <Skeleton still className="h-4 w-3/5" />
                      </span>
                    ) : (
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-lg font-bold text-app-text">
                          {next[0] ? next[0].title || "Scheduled meeting" : "Nothing upcoming"}
                        </span>
                        <span className="block truncate text-[15px] text-app-text-secondary">
                          {next[0]
                            ? `${format(new Date(next[0].scheduled_at), "EEE d MMM 'at' HH:mm")}${
                                next.length > 1 ? `  ·  ${next.length - 1} more scheduled` : ""
                              }`
                            : "See past and cancelled meetings"}
                        </span>
                      </span>
                    )}
                    <ChevronRight className="h-5 w-5 shrink-0 text-app-text-secondary" />
                  </Link>
                </div>
                {/* Desktop: the column has room for the next five. */}
                {next.length > 0 ? (
                  <div className="hidden lg:block">
                    {next.slice(0, 5).map((m) => (
                      <Link
                        key={m.id}
                        to={`${base}/meetings/scheduled`}
                        className="flex items-center gap-3 rounded-2xl py-2 transition-opacity hover:opacity-80"
                      >
                        <CalendarClock className="h-6 w-6 shrink-0 text-brand" strokeWidth={1.5} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[17px] font-bold text-app-text">
                            {m.title || "Scheduled meeting"}
                          </span>
                          <span className="block truncate text-sm text-app-text-secondary">
                            {format(new Date(m.scheduled_at), "EEE d MMM 'at' HH:mm")}
                          </span>
                        </span>
                      </Link>
                    ))}
                    <Link
                      to={`${base}/meetings/scheduled`}
                      className="mt-1 flex items-center gap-1 py-2 text-sm font-semibold text-brand"
                    >
                      {next.length > 5
                        ? `All ${next.length} scheduled`
                        : "See past and cancelled meetings"}
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                ) : null}
                {allCompany ? null : (
                  <CalendarMeetingsPanel
                    calendar={calendar}
                    meetings={calendarMeetings}
                    onRecord={(prefill) =>
                      navigate(`${base}/meetings/new`, { state: { fromCalendar: prefill } })
                    }
                  />
                )}
              </div>
            </aside>

            <div className="min-w-0 lg:order-1">
              {/* Sections */}
              {/* Loading: skeleton rows if this list had meetings last time,
                  else blank, then the meetings or the empty state. */}
              {list.isLoading ? (
                meetingsSkeleton ? <MeetingsListSkeleton /> : null
              ) : list.error ? (
                <div className="mt-8">
                  <p className="text-[15px] text-app-text-secondary">
                    Could not load your meetings.
                  </p>
                  <button
                    type="button"
                    onClick={() => void list.refetch()}
                    className="mt-2 text-sm font-semibold text-brand"
                  >
                    Try again
                  </button>
                </div>
              ) : meetings.length === 0 ? (
                <EmptyState
                  shape="meeting"
                  animate={!(urlSearch || filterTags.length || dateActive || employeeId)}
                  title={
                    employeeId && !urlSearch && !filterTags.length
                      ? "No meetings for this person"
                      : teamScope && !urlSearch && !filterTags.length && !dateActive
                      ? TEAM_EMPTY_TITLE
                      : urlSearch || filterTags.length
                      ? "No matches"
                      : dateActive
                        ? "Nothing in this period"
                        : "No boardrooms yet"
                  }
                  description={
                    employeeId && !urlSearch && !filterTags.length
                      ? "They haven't recorded a boardroom yet. Pick another employee or All employees."
                      : teamScope && !urlSearch && !filterTags.length && !dateActive
                      ? "Meetings your team records show here."
                      : urlSearch || filterTags.length
                      ? "No meetings match your search or tags."
                      : dateActive
                        ? "Pick another time period to see more meetings."
                        : "Start a meeting from Setup, or finish a recording to see it here."
                  }
                />
              ) : (
                sections.map((section) => (
                  <section key={section.label}>
                    <h2 className="mb-3 mt-5 text-lg font-semibold tracking-[-0.5px] text-app-text/40">
                      {section.label}
                    </h2>
                    {section.items.map((m, i) => (
                      <MeetingTimelineRow
                        key={m.id}
                        meeting={m}
                        first={i === 0}
                        last={i === section.items.length - 1}
                        base={base}
                        scope={scope}
                        teamScope={teamScope}
                        tagsMenu={(mt) => (
                          <TagsSubmenu
                            tags={mt.tags ?? []}
                            suggestions={availableTags.map((tag) => ({ tag, uses: 0 }))}
                            save={(next) => saveTags.mutateAsync({ id: mt.id, tags: next })}
                          />
                        )}
                      />
                    ))}
                  </section>
                ))
              )}
              <div ref={sentinel} className="h-1" />
              {list.isFetchingNextPage ? (
<div className="flex justify-center py-4" role="status" aria-label="Loading more">
                  <Loader2 className="h-5 w-5 animate-spin text-brand" aria-hidden />
                </div>
              ) : null}
            </div>
          </div>

          {/* Dock: a wide search + a round "+" (New boardroom), as the phone
              (DES 08-10-2026: "search ko new boardroom jitna bara karo, aur
              new boardroom main sirf + laga kar gool kar do"). The + is brand
              red in light, THE dock glass with a red + in dark; THE dock fade
              behind the row. */}
          <div className={cn("pointer-events-none sticky bottom-6 z-20 mt-auto flex justify-center gap-3 pt-8", dockFadeBehind)}>
            {/* The Notes screen's search (DES 01-10-2026), the shared one. */}
            <ExpandingSearch
              value={draft}
              onChange={setDraft}
              placeholder="Search meetings"
              label="Search meetings"
              alwaysOpen
              openWidth="w-[min(22rem,calc(100vw-7.5rem))]"
              className="pointer-events-auto"
            />
            <Link
              to={`${base}/meetings/new`}
              aria-label="New boardroom"
              title="New boardroom"
              className={cn(
                "pointer-events-auto flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand text-white shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-colors hover:bg-brand/90",
                dockGlassDark,
                "dark:text-brand",
              )}
            >
              <Plus className="h-6 w-6" strokeWidth={2} />
            </Link>
          </div>
        </>
      )}

      {/* The Cmd+K palette's look (DES 01-10-2026). */}
      <AskPalette open={askOpen} onOpenChange={setAskOpen}>
        <AskMeetingsPanel meetingHref={(id) => `${base}/meetings/${id}`} />
      </AskPalette>
    </div>
  );
}

export default MeetingsLibraryPage;
